/**
 * Browser-storage half of "restore the open right-side tool panel on reload".
 *
 * Two backends are needed because the two reload cases are genuinely different:
 *
 *   - Reopening SAVED WORK (or a share code) goes through the save file, so the
 *     selection rides along in `WorkspaceData.openPanelSelection`. That travels
 *     with the work and across devices.
 *   - A plain browser refresh never goes through a save, so nothing would be
 *     written. That case is covered here, in `localStorage`, keyed per activity.
 *
 * Precedence on load is "save state wins": a value that came from the save file
 * describes the work the user deliberately opened, whereas localStorage only
 * remembers what this browser was last looking at.
 *
 * What is stored is a SELECTION (a widget id), not a panel id — "which panel is
 * open" is derived from `selectedTool` and has never been state of its own.
 */
var KEY_PREFIX = 'mlc:openPanel:';
var keyFor = function (activityId) { return "".concat(KEY_PREFIX).concat(activityId); };
/**
 * Every access is wrapped: Safari private mode throws on `localStorage` access,
 * and a corrupt/hand-edited value must not take the player down on boot. A
 * failure here means "no restore", never an error.
 */
export var readOpenPanels = function (activityId) {
    if (!activityId)
        return {};
    try {
        var raw = window.localStorage.getItem(keyFor(activityId));
        if (!raw)
            return {};
        var parsed_1 = JSON.parse(raw);
        if (!parsed_1 || typeof parsed_1 !== 'object' || Array.isArray(parsed_1))
            return {};
        // Keep only string values — a malformed entry must not reach setSelectedTool.
        var out_1 = {};
        Object.keys(parsed_1).forEach(function (k) {
            if (typeof parsed_1[k] === 'string' && parsed_1[k])
                out_1[k] = parsed_1[k];
        });
        return out_1;
    }
    catch (_a) {
        return {};
    }
};
/**
 * Record (or clear, when `selectionId` is null) the open panel for one page.
 * Clearing matters: without it, deselecting would leave the last panel stored
 * and it would spring back open on the next reload.
 */
export var writeOpenPanel = function (activityId, page, selectionId) {
    if (!activityId)
        return;
    try {
        var current = readOpenPanels(activityId);
        if (selectionId)
            current[String(page)] = selectionId;
        else
            delete current[String(page)];
        if (Object.keys(current).length === 0)
            window.localStorage.removeItem(keyFor(activityId));
        else
            window.localStorage.setItem(keyFor(activityId), JSON.stringify(current));
    }
    catch (_a) {
        // Storage unavailable (private mode / quota). Restoring the panel is a
        // convenience, so failing to remember it is not worth surfacing.
    }
};
